/**
 * paso-datos-generales-helpers.ts — pure helpers for PasoDatosGenerales seeding.
 *
 * REQ-7a: seedClienteFromReport — fills cliente from informe.cliente when dg.cliente is empty.
 * REQ-7b: seedEmisionDefault — seeds emision with today's ISO date when empty.
 * REQ-12: OT_LABEL — display label for the otMpsa field ("OT", not "OT MPSA").
 * FIX-1: correlativoDeNumero — extracts 3-digit correlativo from número string.
 *
 * // ponytail: helpers + one constant; no new component, no new state.
 */
import type { IeDatosGenerales } from "../types.js";

/**
 * REQ-12: display label for the `otMpsa` payload field.
 * The payload key is intentionally preserved; only the label changes (S-12.1..S-12.3).
 * // ponytail: one exported string constant — no migration needed.
 */
export const OT_LABEL = "OT" as const;

/**
 * Default cargo, pre-filled on every report and editable by the user. Surfaces in
 * the PDF cierre block next to the supervisor's name.
 */
export const DEFAULT_CARGO = "Supervisor de equipos y servicios" as const;

/**
 * Seeds `datosGenerales.cliente` from the informe's clienteNombre (or free-text
 * cliente) when the stored value is empty. If already set, preserves it.
 *
 * S-7a.1: client field is pre-filled from the report's client name.
 * S-7a.2: free-text clients (no clienteId) use the raw nombre stored on the informe.
 */
export function seedClienteFromReport(
  dg: IeDatosGenerales,
  clienteNombre: string,
): IeDatosGenerales {
  if (dg.cliente) return dg; // already set — preserve
  return { ...dg, cliente: clienteNombre };
}

/**
 * Extracts the 3-digit correlativo from a report número.
 *
 * Format: `{TIPO}{YY}{INITIALS}{CORR3}-{SIGLA}` e.g. `IE26AM002-TC`
 * Returns the 3 digits immediately before the last `-` separator.
 * Returns `""` when the número has no matching pattern (local/unsynced draft).
 *
 * FIX-1: used by inyectarAutomaticos to set anexo from número.
 */
export function correlativoDeNumero(numero: string): string {
  const m = /(\d{3})-[^-]*$/.exec(numero);
  return m ? m[1]! : "";
}

/**
 * Seeds `datosGenerales.emision` with today's ISO date (YYYY-MM-DD) when empty.
 * If already set, preserves the existing value.
 *
 * S-7b.1: new reports get today as emision default.
 * S-7b.2: existing non-empty emision is not overwritten.
 */
export function seedEmisionDefault(dg: IeDatosGenerales): IeDatosGenerales {
  if (dg.emision) return dg; // already set — preserve
  return { ...dg, emision: new Date().toISOString().slice(0, 10) };
}
